import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SERVER_EVENTS } from '@superchat/shared/events';
import { TICKET_STATUSES, TICKET_PRIORITIES } from '@superchat/shared/constants';
import { Search, Plus, Trash2, X, Paperclip } from 'lucide-react';
import { useWorkspace } from '../lib/workspace.jsx';
import { api } from '../lib/api.js';
import { shortTime, STATUS_LABEL, PRIORITY_LABEL, initials } from '../lib/format.js';
import { useUploads, uploadFile, formatBytes } from '../lib/upload.js';
import TicketAttachments from '../components/TicketAttachments.jsx';
import TicketDialog from '../components/TicketDialog.jsx';
import { upsertTicket } from '../lib/tickets.js';

const PRIORITY_CLASS = {
  urgent: 'pri pri--p1',
  high: 'pri pri--p2',
  normal: 'pri pri--p3',
  low: 'pri pri--p4',
};

const STATUS_CLASS = {
  open: 'pill pill--accent',
  in_progress: 'pill pill--sel',
  waiting: 'pill pill--warn',
  resolved: 'pill pill--ok',
  closed: 'pill pill--muted',
};

/** How long the ticket has been open — the real stand-in for an SLA clock. */
function age(ticket) {
  const end = ticket.resolvedAt ?? ticket.closedAt;
  if (end) return '—';
  const hours = (Date.now() - new Date(ticket.createdAt)) / 3600000;
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

let seq = 0;
const nextClientId = () =>
  `t_${Date.now().toString(36)}_${(seq += 1)}_${Math.random().toString(36).slice(2, 8)}`;

function NewTicketDialog({ apps, defaultAppId, conversations, membersByApp, uploads, onClose, onCreated }) {
  // Which tenant the ticket is filed against. A single-app scope has one
  // answer and hides the question; the all-apps scope has to ask, because a
  // ticket, its number and its assignees all belong to exactly one app.
  const [appId, setAppId] = useState(defaultAppId ?? apps[0]?.id ?? '');
  const members = membersByApp[appId] ?? [];
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('normal');
  const [conversationId, setConversationId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const picker = useRef(null);
  /**
   * One id for this dialog, minted once and reused by every attempt.
   *
   * `busy` disables the button, but state does not update until React
   * re-renders and a fast double-click lands inside that gap — so the server
   * is told which submission this is rather than being trusted to receive
   * exactly one.
   */
  const clientId = useRef(nextClientId());
  const submitting = useRef(false);

  /* Uploaded as they are picked, so creating the ticket is one fast call. */
  const attach = async (picked) => {
    const list = Array.from(picked ?? []).filter(Boolean);
    setError(null);
    for (const file of list) {
      if (uploads.maxBytes && file.size > uploads.maxBytes) {
        setError(`${file.name} is larger than ${formatBytes(uploads.maxBytes)}`);
        continue;
      }
      setFiles((current) => [...current, { name: file.name, size: file.size, uploading: true }]);
      try {
        const uploaded = await uploadFile(appId, file, { context: 'ticket' });
        setFiles((current) =>
          current.map((f) => (f.name === file.name && f.uploading ? uploaded : f))
        );
      } catch (err) {
        setFiles((current) => current.filter((f) => !(f.name === file.name && f.uploading)));
        setError(err.message);
      }
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    // A ref, not the `busy` flag: this one is true before React re-renders.
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      const ticket = await api.tickets.create(appId, {
        subject,
        description,
        priority,
        conversationId: conversationId || null,
        // Assigning at creation is the common case — the ticket exists because
        // someone specific needs to pick it up — and it notifies them the same
        // way a later reassignment does.
        assigneeId: assigneeId || null,
        attachments: files
          .filter((f) => f.key)
          .map(({ key, name, mimeType, size }) => ({ key, name, mimeType, size })),
        clientId: clientId.current,
      });
      onCreated(ticket);
      onClose();
    } catch (err) {
      setError(err.message);
      // Let them fix the problem and try again — the same id, so a request
      // that did land despite the error is not filed twice.
      submitting.current = false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <form className="dialog" onMouseDown={(e) => e.stopPropagation()} onSubmit={submit}>
        <header className="dialog__head">
          <h2>New ticket</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </header>

        <label className="field">
          <span className="field__label">SUBJECT</span>
          <input
            className="field__control"
            required
            maxLength={300}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">DESCRIPTION</span>
          <textarea
            className="field__control field__control--area"
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <div className="dialog__grid">
          {apps.length > 1 && (
            <label className="field">
              <span className="field__label">APP</span>
              <select
                className="field__control"
                value={appId}
                onChange={(e) => {
                  setAppId(e.target.value);
                  // Both belong to the app that was selected a moment ago.
                  setConversationId('');
                  setAssigneeId('');
                }}
              >
                {apps.map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="field">
            <span className="field__label">PRIORITY</span>
            <select
              className="field__control"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              {TICKET_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABEL[p]} · {p}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">ASSIGN TO</span>
            <select
              className="field__control"
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
            >
              <option value="">Unassigned</option>
              {members.map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.name || member.email}
                  {member.isOwner ? ' · owner' : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">LINKED CONVERSATION</span>
            <select
              className="field__control"
              value={conversationId}
              onChange={(e) => setConversationId(e.target.value)}
            >
              <option value="">None</option>
              {/* Only threads on the same app: the server rejects a link that
                  crosses tenants, and offering one would be a lie. */}
              {conversations
                .filter((c) => c.appId === appId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.visitor.name || c.visitor.visitorId} — {c.lastMessagePreview?.slice(0, 40)}
                  </option>
                ))}
            </select>
          </label>
        </div>

        {uploads.enabled && (
          <div className="field">
            <span className="field__label">ATTACHMENTS</span>
            <div className="ticket-files">
              {files.map((file, index) => (
                <div className="ticket-file" key={`${file.name}-${index}`}>
                  <Paperclip size={11} />
                  <span className="ticket-file__name">{file.name}</span>
                  <span className="file__size">
                    {file.uploading ? 'Uploading…' : formatBytes(file.size)}
                  </span>
                  {!file.uploading && (
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => setFiles((current) => current.filter((f) => f !== file))}
                      aria-label={`Remove ${file.name}`}
                    >
                      <X size={11} />
                    </button>
                  )}
                </div>
              ))}
              <input
                ref={picker}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  attach(e.target.files);
                  e.target.value = '';
                }}
              />
              <button type="button" className="macro" onClick={() => picker.current?.click()}>
                <Plus size={11} /> Attach a file
              </button>
            </div>
          </div>
        )}

        {error && <div className="login__error">{error}</div>}

        <footer className="dialog__foot">
          <button type="button" className="button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={busy || !subject || files.some((f) => f.uploading)}
          >
            Create ticket
          </button>
        </footer>
      </form>
    </div>
  );
}

export function TicketsView() {
  const { activeAppId, allApps, appIndex, scopedApps, conversations, membersByApp, onSocketEvent } =
    useWorkspace();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState(false);
  // Which ticket has its evidence panel open. One at a time: the table is the
  // overview, and two open panels stop it being one.
  const [expanded, setExpanded] = useState(null);
  // Which ticket is open in full. Held by id rather than by value so the
  // dialog follows another agent's edit to the same ticket.
  const [detail, setDetail] = useState(null);
  const uploads = useUploads();

  // The set of apps in scope, as a stable string — the loader below re-runs
  // when the scope changes, not on every re-render of the same scope.
  const scopeKey = useMemo(() => scopedApps.map((a) => a.id).join(','), [scopedApps]);

  /**
   * One request per app in scope, merged. There is no cross-tenant ticket
   * endpoint by design; an app that fails drops out of the merge rather than
   * emptying the table.
   */
  const load = useCallback(async () => {
    const appIds = scopeKey ? scopeKey.split(',') : [];
    if (appIds.length === 0) {
      setTickets([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const pages = await Promise.all(
        appIds.map((appId) =>
          api.tickets
            .list(appId, { limit: 100 })
            .then(({ data }) => (Array.isArray(data) ? data : []))
            .catch(() => [])
        )
      );
      setTickets(pages.flat().sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
    } catch {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [scopeKey]);

  useEffect(() => {
    load();
  }, [load]);

  /** The one way a ticket enters or moves in this list. See lib/tickets.js. */
  const applyTicket = useCallback((ticket) => {
    setTickets((current) => upsertTicket(current, ticket));
  }, []);

  /* Another agent's edit lands here without a refresh. */
  useEffect(
    () =>
      onSocketEvent((event, payload) => {
        if (event !== SERVER_EVENTS.TICKET_UPDATED) return;
        applyTicket(payload);
      }),
    [onSocketEvent, applyTicket]
  );

  /* Every write goes to the ticket's own app, never to an ambient one. */
  const patch = async (ticket, changes) => {
    const updated = await api.tickets.update(ticket.appId, ticket.id, changes);
    applyTicket(updated);
    return updated;
  };

  const remove = async (ticket) => {
    await api.tickets.remove(ticket.appId, ticket.id);
    setTickets((current) => current.filter((t) => t.id !== ticket.id));
  };

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return tickets.filter((t) => {
      if (filter === 'open' && ['resolved', 'closed'].includes(t.status)) return false;
      if (filter === 'unassigned' && t.assigneeId) return false;
      if (filter === 'urgent' && t.priority !== 'urgent') return false;
      if (!needle) return true;
      return `${t.subject} ${t.description}`.toLowerCase().includes(needle);
    });
  }, [tickets, filter, query]);

  const counts = useMemo(
    () => ({
      all: tickets.length,
      open: tickets.filter((t) => !['resolved', 'closed'].includes(t.status)).length,
      unassigned: tickets.filter((t) => !t.assigneeId).length,
      urgent: tickets.filter((t) => t.priority === 'urgent').length,
    }),
    [tickets]
  );

  /**
   * Resolved from the list rather than held in state, so the open dialog shows
   * another agent's edit as it arrives — and closes itself if they delete the
   * ticket out from under it.
   */
  const detailTicket = useMemo(
    () => (detail ? (tickets.find((t) => t.id === detail) ?? null) : null),
    [detail, tickets]
  );

  useEffect(() => {
    if (detail && !detailTicket) setDetail(null);
  }, [detail, detailTicket]);

  const conversationLabel = (id) => {
    const conversation = conversations.find((c) => c.id === id);
    return conversation ? conversation.visitor.name || conversation.visitor.visitorId : '—';
  };

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">Tickets</h1>
        <span className="page__sub">
          {counts.open} open · {counts.urgent} urgent
        </span>
        <div className="page__actions">
          <div className="search search--sm">
            <Search size={13} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter tickets…"
              aria-label="Filter tickets"
            />
          </div>
          <button
            type="button"
            className="button button--primary"
            onClick={() => setDialog(true)}
            disabled={scopedApps.length === 0}
          >
            <Plus size={13} /> New ticket
          </button>
        </div>
      </header>

      <div className="filterbar">
        {['all', 'open', 'unassigned', 'urgent'].map((key) => (
          <button
            key={key}
            type="button"
            className={`filter${filter === key ? ' is-active' : ''}${
              key === 'urgent' ? ' filter--danger' : ''
            }`}
            onClick={() => setFilter(key)}
          >
            {key[0].toUpperCase() + key.slice(1)} <span>{counts[key]}</span>
          </button>
        ))}
      </div>

      <div className={`table table--tickets${allApps ? ' table--multiapp' : ''}`}>
        <div className="table__head">
          <div>TICKET</div>
          {allApps && <div>APP</div>}
          <div>SUBJECT</div>
          <div>CONVERSATION</div>
          <div>PRI</div>
          <div>STATUS</div>
          <div>ASSIGNEE</div>
          <div>AGE</div>
          <div>UPDATED</div>
          <div />
        </div>

        <div className="table__body">
          {loading && <div className="table__empty">Loading tickets…</div>}
          {!loading && visible.length === 0 && (
            <div className="table__empty">No tickets match this filter.</div>
          )}

          {visible.map((ticket) => {
            const urgent = ticket.priority === 'urgent' && !['resolved', 'closed'].includes(ticket.status);
            const open = expanded === ticket.id;
            const fileCount = ticket.attachments?.length ?? 0;
            return (
              <div key={ticket.id}>
              <div className={`table__row${urgent ? ' is-danger' : ''}`}>
                {/* Ticket numbers restart at 1 per app, so SC-14 is ambiguous
                    the moment two tenants are on screen together. */}
                <div className="mono muted" data-cell="id">SC-{ticket.number}</div>
                {allApps && (
                  <div className="table__app ellipsis" data-cell="app" title={appIndex[ticket.appId]?.name}>
                    <span
                      className="table__app-stripe"
                      style={{ background: appIndex[ticket.appId]?.color }}
                    />
                    {appIndex[ticket.appId]?.name ?? '—'}
                  </div>
                )}
                <div className="table__subject" data-cell="subject" title={ticket.description || ticket.subject}>
                  {/* The way into the whole ticket. The inline selects beside
                      it stay for triage — most edits are one field and do not
                      deserve a dialog — but everything else lives in there. */}
                  <button
                    type="button"
                    className="table__subject-text table__subject-open"
                    onClick={() => setDetail(ticket.id)}
                    title="Open this ticket"
                  >
                    {ticket.subject}
                  </button>
                  {/* Files are the reason to open the row, so the count is the
                      affordance rather than a bare chevron. */}
                  <button
                    type="button"
                    className={`table__files${open ? ' is-active' : ''}`}
                    onClick={() => setExpanded(open ? null : ticket.id)}
                    title={open ? 'Hide attachments' : 'Show attachments'}
                  >
                    <Paperclip size={11} />
                    {fileCount > 0 ? fileCount : ''}
                  </button>
                </div>
                <div className="muted ellipsis" data-cell="conversation">
                  {conversationLabel(ticket.conversationId)}
                </div>
                <div data-cell="priority">
                  <select
                    className={PRIORITY_CLASS[ticket.priority]}
                    value={ticket.priority}
                    onChange={(e) => patch(ticket, { priority: e.target.value })}
                    aria-label="Priority"
                  >
                    {TICKET_PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {PRIORITY_LABEL[p]}
                      </option>
                    ))}
                  </select>
                </div>
                <div data-cell="status">
                  <select
                    className={STATUS_CLASS[ticket.status]}
                    value={ticket.status}
                    onChange={(e) => patch(ticket, { status: e.target.value })}
                    aria-label="Status"
                  >
                    {TICKET_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABEL[s]}
                      </option>
                    ))}
                  </select>
                </div>
                <div data-cell="assignee">
                  <select
                    className="select"
                    value={ticket.assigneeId ?? ''}
                    onChange={(e) => patch(ticket, { assigneeId: e.target.value || null })}
                    aria-label="Assignee"
                  >
                    <option value="">Unassigned</option>
                    {/* The roster of this ticket's own app: a colleague who
                        cannot reach the tenant cannot be given its work. */}
                    {(membersByApp[ticket.appId] ?? []).map((member) => (
                      <option key={member.userId} value={member.userId}>
                        {member.name || member.email || initials(member.userId)}
                        {member.isOwner ? ' · owner' : ''}
                      </option>
                    ))}
                    {/* Assigned to someone since removed from the app. Without
                        this the select falls back to the first option and the
                        row reads as unassigned, which it is not. */}
                    {ticket.assigneeId &&
                      !(membersByApp[ticket.appId] ?? []).some(
                        (m) => m.userId === ticket.assigneeId
                      ) && (
                        <option value={ticket.assigneeId}>Former member</option>
                      )}
                  </select>
                </div>
                <div className="mono" data-cell="age">{age(ticket)}</div>
                <div className="mono muted" data-cell="updated">{shortTime(ticket.updatedAt)}</div>
                <div data-cell="actions">
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => remove(ticket)}
                    aria-label="Delete ticket"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>

              {open && (
                <div className="table__drawer">
                  <TicketAttachments
                    appId={ticket.appId}
                    ticket={ticket}
                    uploads={uploads}
                    onChange={(updated) =>
                      setTickets((current) =>
                        current.map((t) => (t.id === updated.id ? updated : t))
                      )
                    }
                  />
                </div>
              )}
              </div>
            );
          })}
        </div>
      </div>

      {dialog && (
        <NewTicketDialog
          apps={scopedApps}
          defaultAppId={activeAppId ?? scopedApps[0]?.id}
          conversations={conversations}
          membersByApp={membersByApp}
          uploads={uploads}
          onClose={() => setDialog(false)}
          onCreated={applyTicket}
        />
      )}

      {detailTicket && (
        <TicketDialog
          appId={detailTicket.appId}
          ticket={detailTicket}
          members={membersByApp[detailTicket.appId] ?? []}
          // Only threads on the ticket's own app can be linked to it.
          conversations={conversations.filter((c) => c.appId === detailTicket.appId)}
          uploads={uploads}
          onClose={() => setDetail(null)}
          onSaved={applyTicket}
          onDeleted={(ticket) =>
            setTickets((current) => current.filter((t) => t.id !== ticket.id))
          }
        />
      )}
    </div>
  );
}

export default TicketsView;
